import { createTheme, Theme } from '@mui/material/styles';

// Body font (Inter)
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
// Heading font (Space Grotesk)
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/600.css';
import '@fontsource/space-grotesk/700.css';

const bodyFont = '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const headingFont = `"Space Grotesk", ${bodyFont}`;

// Pull breakpoints from a base theme so we can define responsive variant sizes
// directly inside typography — no per-component sx overrides needed.
const { breakpoints } = createTheme();

export type ThemeMode = 'light' | 'dark';

export function makeTheme(mode: ThemeMode): Theme {
  const isDark = mode === 'dark';

  const borderColor = isDark ? 'rgba(255, 255, 255, 0.10)' : 'rgba(15, 17, 21, 0.08)';
  const borderHover = isDark ? 'rgba(255, 255, 255, 0.20)' : 'rgba(15, 17, 21, 0.16)';
  const shadowColor = isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(15, 17, 21, 0.08)';

  const shadow = (y: number, blur: number, alpha: number) =>
    `0 ${y}px ${blur}px ${isDark ? `rgba(0, 0, 0, ${alpha * 2})` : `rgba(15, 17, 21, ${alpha})`}`;

  return createTheme({
    palette: {
      mode,
      primary: {
        light: '#E54442',
        main: '#C1201F',
        dark: '#8C0D0C',
        contrastText: '#ffffff',
      },
      secondary: {
        light: isDark ? '#CFCFCF' : '#B0B0B0',
        main: isDark ? '#9A9A9A' : '#6B6B6B',
        dark: isDark ? '#6B6B6B' : '#3D3D3D',
        contrastText: '#ffffff',
      },
      background: isDark
        ? { default: '#0E0F11', paper: '#16181C' }
        : { default: '#FAFAFA', paper: '#ffffff' },
      text: isDark
        ? { primary: '#F2F3F5', secondary: '#A1A7B3' }
        : { primary: '#0F1115', secondary: '#5B6470' },
      divider: borderColor,
    },
    shape: {
      borderRadius: 6,
    },
    typography: {
      fontFamily: bodyFont,
      h1: {
        fontFamily: headingFont,
        fontWeight: 700,
        letterSpacing: '-0.02em',
        lineHeight: 1.05,
        fontSize: '2.5rem',
        [breakpoints.up('sm')]: { fontSize: '3.5rem' },
        [breakpoints.up('md')]: { fontSize: '4.5rem' },
      },
      h2: {
        fontFamily: headingFont,
        fontWeight: 700,
        letterSpacing: '-0.02em',
        lineHeight: 1.1,
        fontSize: '2rem',
        [breakpoints.up('sm')]: { fontSize: '2.5rem' },
        [breakpoints.up('md')]: { fontSize: '3rem' },
      },
      h3: {
        fontFamily: headingFont,
        fontWeight: 700,
        letterSpacing: '-0.01em',
        lineHeight: 1.15,
        fontSize: '1.75rem',
        [breakpoints.up('md')]: { fontSize: '2rem' },
      },
      h4: {
        fontFamily: headingFont,
        fontWeight: 700,
        letterSpacing: '-0.01em',
        lineHeight: 1.2,
        fontSize: '1.5rem',
      },
      h5: {
        fontFamily: headingFont,
        fontWeight: 700,
        lineHeight: 1.2,
        fontSize: '1.25rem',
      },
      h6: {
        fontFamily: headingFont,
        fontWeight: 600,
        lineHeight: 1.35,
        fontSize: '1.125rem',
      },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
    },
    shadows: [
      'none',
      shadow(1, 2, 0.04),
      shadow(2, 4, 0.06),
      shadow(4, 8, 0.06),
      shadow(6, 12, 0.08),
      shadow(8, 16, 0.08),
      shadow(10, 20, 0.10),
      shadow(12, 24, 0.10),
      shadow(14, 28, 0.12),
      shadow(16, 32, 0.12),
      shadow(18, 36, 0.14),
      shadow(20, 40, 0.14),
      shadow(22, 44, 0.16),
      shadow(24, 48, 0.16),
      shadow(26, 52, 0.18),
      shadow(28, 56, 0.18),
      shadow(30, 60, 0.20),
      shadow(32, 64, 0.20),
      shadow(34, 68, 0.22),
      shadow(36, 72, 0.22),
      shadow(38, 76, 0.24),
      shadow(40, 80, 0.24),
      shadow(42, 84, 0.26),
      shadow(44, 88, 0.26),
      shadow(46, 92, 0.28),
    ] as Theme['shadows'],
    components: {
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderBottom: `1px solid ${borderColor}`,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: 6,
            paddingInline: 16,
          },
          sizeLarge: {
            paddingBlock: 12,
            paddingInline: 22,
            fontSize: '1rem',
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            border: `1px solid ${borderColor}`,
            borderRadius: 8,
            overflow: 'hidden',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            transition: 'transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease',
            '&:hover': {
              borderColor: borderHover,
              boxShadow: `0 10px 20px ${shadowColor}`,
            },
          },
        },
      },
      MuiCardActionArea: {
        styleOverrides: {
          root: {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            flexGrow: 1,
          },
        },
      },
      MuiCardMedia: {
        styleOverrides: {
          root: {
            // When `component="img"` is used the root IS the <img> element —
            // force cover + full width so images never leak background through.
            objectFit: 'cover',
            width: '100%',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 6,
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            fontWeight: 500,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            textTransform: 'none',
            fontWeight: 600,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 10,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
          },
        },
      },
    },
  });
}
